import React, { useState, useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import LineChart from '../../../components/charts/LineChart';
import supabase from '../../../supabase/supabase';
import { cn } from '../../../components/ui/Button';

export default function BarberOverview() {
  const [stats, setStats] = useState({ totalBookings: 0, completed: 0, earnings: 0, rating: 5.0 });
  const [loading, setLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const syncAndFetchData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Lazy Sync: Auto-complete bookings confirmed but older than 24h past scheduled time
        await supabase
          .from('bookings_1740423033529')
          .update({ status: 'completed', payment_status: 'paid' })
          .eq('barber_id', user.id)
          .eq('status', 'confirmed')
          .lt('booking_date', new Date(Date.now() - 86400000).toISOString().split('T')[0]);

        const { count: bookingCount } = await supabase
          .from('bookings_1740423033529')
          .select('*', { count: 'exact', head: true })
          .eq('barber_id', user.id);

        const { count: completedCount } = await supabase
          .from('bookings_1740423033529')
          .select('*', { count: 'exact', head: true })
          .eq('barber_id', user.id)
          .eq('status', 'completed');

        // Fetch earnings from completed bookings (cash is collected on completion)
        const { data: completedBookings } = await supabase
          .from('bookings_1740423033529')
          .select('total_price')
          .eq('barber_id', user.id)
          .eq('status', 'completed');

        const totalEarnings = completedBookings?.reduce((sum, b) => sum + Number(b.total_price), 0) || 0;

        const { data: profile } = await supabase
          .from('profiles_1740423033529')
          .select('rating, is_available')
          .eq('id', user.id)
          .single();

        setStats({
          totalBookings: bookingCount || 0,
          completed: completedCount || 0,
          earnings: totalEarnings,
          rating: profile?.rating || 5.0
        });
        setIsAvailable(profile?.is_available ?? true);
      } catch (err) {
        console.error("Dashboard Sync Error:", err);
      } finally {
        setLoading(false);
      }
    };

    syncAndFetchData();
  }, []);

  const toggleStatus = async () => {
    setUpdatingStatus(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const newStatus = !isAvailable;
    const { error } = await supabase
      .from('profiles_1740423033529')
      .update({ is_available: newStatus })
      .eq('id', user.id);

    if (!error) {
      setIsAvailable(newStatus);
    }
    setUpdatingStatus(false);
  };

  const statCards = [
    { label: 'Total Bookings', value: stats.totalBookings.toString(), icon: 'Calendar', color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Completed', value: stats.completed.toString(), icon: 'CheckSquare', color: 'text-green-500', bg: 'bg-green-50' },
    { label: 'Total Earnings', value: `Rs. ${stats.earnings.toLocaleString()}`, icon: 'DollarSign', color: 'text-brand-primary', bg: 'bg-brand-primary/10' },
    { label: 'Rating', value: stats.rating.toString(), icon: 'Star', color: 'text-orange-500', bg: 'bg-orange-50' },
  ];

  return (
    <div className="animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Performance</h1>
          <p className="text-gray-500 font-medium italic mt-1">Real-time business insights for Sri Lanka.</p>
        </div>

        {/* Barber Online / Offline Toggle */}
        <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-gray-100 shadow-sm animate-in slide-in-from-right-4">
          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Status</span>
          <button
            onClick={toggleStatus}
            disabled={updatingStatus || loading}
            className={cn(
              "relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none disabled:opacity-50",
              isAvailable ? "bg-green-500" : "bg-gray-300"
            )}
            title={isAvailable ? "You are visible to customers" : "You are hidden from searches"}
          >
            <div className={cn(
              "absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300",
              isAvailable ? "left-8" : "left-1"
            )} />
          </button>
          <span className={cn(
            "text-xs font-bold w-16 text-center",
            isAvailable ? "text-green-600" : "text-gray-500"
          )}>
            {isAvailable ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white p-7 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5">
            <div className={`w-14 h-14 rounded-2xl ${stat.bg} flex items-center justify-center flex-shrink-0`}>
              <SafeIcon name={stat.icon} className={`text-2xl ${stat.color}`} />
            </div>
            <div>
              <p className="text-2xl font-black text-brand-dark tracking-tight">{stat.value}</p>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-0.5">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <div className="xl:col-span-2 bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
          <h2 className="text-xl font-black text-brand-dark mb-6">Earnings Growth (LKR)</h2>
          <LineChart
            data={stats.earnings === 0 ? [0, 0, 0, 0, 0, 0, 0] : [0, 0, 0, 0, 0, 0, stats.earnings]}
            title="Earnings"
            color="#0047FF"
          />
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
          <h2 className="text-xl font-black text-brand-dark mb-6">Quick Tracking</h2>
          <div className="space-y-4">
            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Profile Views</p>
              <p className="text-2xl font-black text-brand-dark mt-1">0</p>
            </div>
            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Customer Retention</p>
              <p className="text-2xl font-black text-brand-dark mt-1">0%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}